import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  User, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth, getUserWithTenant } from '../firebase/config';
import { dataService } from '../services/dataService';
import { retryWithMobileOptimization, logMobileDebugInfo, createMobileErrorReport } from '../utils/mobileDebug';

// Utility function to detect mobile browsers
const detectMobileBrowser = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Note: retryOperation function removed as it's not used

interface TenantData {
  id: string;
  name: string;
  businessName: string;
  email: string;
  phone?: string;
  address?: string;
  instagramHandle?: string;
  isQrOrderingEnabled?: boolean;
  posSimplified?: boolean;
  workspacePlan?: string;
  type?: string;
  contactPerson?: string;
  city?: string;
  state?: string;
  country?: string;
  status?: string;
  createdAt: Date;
  updatedAt: Date;
  subscription?: any;
}

interface UserData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: string;
  permissions?: string[];
  tenantId: string;
  invitedBy?: string;
  joinedVia?: 'signup' | 'invite' | 'qr';
  createdAt: Date;
  updatedAt: Date;
}

interface AuthContextType {
  currentUser: User | null;
  userData: UserData | null;
  tenantData: TenantData | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithPhone: (phone: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshTenantData: () => Promise<void>;
  loading: boolean;
  error: string | null;
  isMobileBrowser: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [tenantData, setTenantData] = useState<TenantData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMobileBrowser] = useState<boolean>(detectMobileBrowser());

  const signup = async (email: string, password: string) => {
    setError(null);
    setLoading(true);
    
    try {
      await retryWithMobileOptimization(
        () => createUserWithEmailAndPassword(auth, email, password),
        3,
        1000,
        'Firebase Auth User Creation'
      );
      console.log('✅ Account created. Waiting for business onboarding.');
      
    } catch (error: any) {
      console.error('❌ Signup error:', error);
      
      // Create mobile error report for debugging
      const errorReport = createMobileErrorReport(error, 'Signup Process');
      logMobileDebugInfo('Signup Error', errorReport);
      
      // Enhanced error messages for mobile browsers
      let errorMessage = 'Signup failed. ';
      
      if (isMobileBrowser) {
        errorMessage += 'Mobile browser detected. ';
      }
      
      if (error.code) {
        switch (error.code) {
          case 'auth/email-already-in-use':
            errorMessage += 'An account with this email already exists.';
            break;
          case 'auth/weak-password':
            errorMessage += 'Password is too weak. Please use at least 6 characters.';
            break;
          case 'auth/invalid-email':
            errorMessage += 'Invalid email address.';
            break;
          case 'auth/network-request-failed':
            errorMessage += 'Network error. Please check your internet connection and try again.';
            break;
          default:
            errorMessage += `Error: ${error.message || 'Unknown error occurred'}`;
        }
      } else {
        errorMessage += error.message || 'Unknown error occurred';
      }
      
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Load user and tenant data
      await loadUserData(user.uid);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const loginWithPhone = async (phone: string, password: string) => {
    try {
      // First, find the user by phone number
      const userResult = await dataService.findUserByPhone(phone);
      
      if (!userResult) {
        throw new Error('No user found with this phone number');
      }

      // Get the user's email from the found user data
      const userEmail = userResult.userData.email;
      
      if (!userEmail) {
        throw new Error('User found but no email associated with this phone number');
      }

      // Now login with email and password
      const userCredential = await signInWithEmailAndPassword(auth, userEmail, password);
      const user = userCredential.user;
      
      // Load user and tenant data
      await loadUserData(user.uid);
    } catch (error) {
      console.error('Phone login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUserData(null);
      setTenantData(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const loadUserData = async (userId: string) => {
    try {
      console.log('🔄 Loading user data for:', userId);
      
      const result = await retryWithMobileOptimization(
        () => getUserWithTenant(userId),
        3,
        1000,
        'User Data Loading'
      );
      
      if (result) {
        console.log('✅ User data loaded successfully:', {
          userId: result.user.id,
          tenantId: result.tenant?.id,
          userRole: (result.user as any).role,
          businessName: (result.tenant as any)?.businessName
        });
        
        setUserData(result.user as UserData);
        const tenant = result.tenant as TenantData | null;
        if (tenant && !tenant.workspacePlan && !tenant.subscription) {
          tenant.workspacePlan = 'free';
        }
        setTenantData(tenant);
        
        // Additional verification for mobile browsers
        if (isMobileBrowser && !result.tenant) {
          console.warn('⚠️ Mobile browser: Tenant data is null, attempting to refresh...');
          await new Promise(resolve => setTimeout(resolve, 2000));
          await refreshTenantData();
        }
      } else {
        console.warn('⚠️ No tenant profile found for user:', userId, '- onboarding required');
        setUserData(null);
        setTenantData(null);
        return;
      }
    } catch (error) {
      // Only show error if it's not a retry scenario during signup
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (!errorMessage.includes('retry') && !errorMessage.includes('attempt')) {
        console.error('❌ Error loading user data:', error);
        setError(`Failed to load user data: ${errorMessage}`);
      } else {
        console.warn('⚠️ Temporary error during user data loading (will retry):', errorMessage);
      }
      setUserData(null);
      setTenantData(null);
    }
  };

  const refreshTenantData = async () => {
    if (currentUser) {
      await loadUserData(currentUser.uid);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        await loadUserData(user.uid);
      } else {
        setUserData(null);
        setTenantData(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: AuthContextType = {
    currentUser,
    userData,
    tenantData,
    login,
    loginWithPhone,
    signup,
    logout,
    refreshTenantData,
    loading,
    error,
    isMobileBrowser
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 